import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { X, AlertTriangle, CheckCircle2, Info, FileUp, UserCheck } from "lucide-react";
import HeadReviewDialog from "@/components/HeadReviewDialog";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

/**
 * Phase-2 Budget V2 upload preview/confirm dialog.
 * Supports both Sanctioned (qty-driven) and Working (amount-driven) flows.
 *
 * Props:
 *   kind: "sanctioned" | "working"
 *   projectId, projectCode, projectName
 *   open, onOpenChange
 *   onSuccess()
 */
export default function BudgetV2UploadDialog({ kind, projectId, projectCode, projectName, open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState("pick"); // pick | review | done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [importing, setImporting] = useState(false);
  const [headReviewOpen, setHeadReviewOpen] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStep("pick"); setFile(null); setPreview(null); setRows([]); setFilter("all");
      setConfirmResult(null);
    }
  }, [open]);

  const runPreview = async (f) => {
    setFile(f);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post(`/v2/budgets/${kind}/preview?project_id=${projectId}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      setRows(r.data.rows.map(x => ({ ...x, amount_choice: x.needs_amount_choice ? "" : (x.amount_choice || "") })));
      setStep("review");
    } catch (e) { toast.error(e.response?.data?.detail || `Preview failed: ${e.message}`); }
  };

  const filteredRows = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "error") return rows.filter(r => r.status === "ERROR");
    if (filter === "new") return rows.filter(r => r.added_in_revision);
    if (filter === "unchanged") return rows.filter(r => r.status === "UNCHANGED");
    return rows;
  }, [rows, filter]);

  const setChoice = (idx, choice) => {
    setRows(rows.map(r => r.row_index === idx ? { ...r, amount_choice: choice } : r));
  };

  const canImport = useMemo(() => {
    if (!preview) return false;
    return preview.can_import;
  }, [preview]);

  const needsHeadReview = preview?.needs_head_review;
  const pendingHeads = preview?.pending_heads || [];

  const doImport = async () => {
    setImporting(true);
    try {
      if (kind === "working") {
        // Working uploads now go through the draft/approval workflow.
        // Re-upload the same file, this time to /drafts, so the server
        // persists a full audit-able draft doc.
        const fd = new FormData();
        fd.append("project_id", projectId);
        fd.append("file", file);
        const r = await api.post("/v2/budgets/working/drafts", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Draft created — awaiting Authorizer approval (${r.data.rows?.length} rows).`);
        setStep("done");
        onSuccess?.();
      } else {
        const payload = {
          project_id: projectId,
          file_name: file?.name || "",
          rows: rows.filter(r => r.status !== "ERROR").map(r => ({
            row_index: r.row_index, period_label: r.period_label, head_code: r.head_code,
            sub_type_code: r.sub_type_code, sub_type_name: r.sub_type_name, unit_label: r.unit_label,
            qty_1: r.qty_1, qty_2: r.qty_2, rate: r.rate, amount: r.amount, notes: r.notes,
            amount_choice: r.amount_choice || "",
          })),
        };
        const r = await api.post(`/v2/budgets/${kind}/confirm`, payload);
        setConfirmResult(r.data);
        toast.success(`Imported ${r.data.sub_types} sub-types · ${r.data.allocations} allocations`);
        setStep("done");
        onSuccess?.();
      }
    } catch (e) { toast.error(e.response?.data?.detail || `Import failed: ${e.message}`); }
    setImporting(false);
  };

  const t = preview?.totals || {};
  const c = preview?.counts || {};
  const isSanc = kind === "sanctioned";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="v2-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="w-5 h-5" />
            {isSanc ? "Sanctioned" : "Working"} Budget Upload · {projectCode} · {projectName}
          </DialogTitle>
        </DialogHeader>

        {step === "pick" && (
          <div className="p-6 border-2 border-dashed border-slate-300 rounded-lg text-center space-y-3">
            <div className="text-sm text-slate-600">
              {isSanc
                ? "Upload the RKF Sanctioned template (.xlsx recommended, .csv accepted). Quantity-driven — Amount must equal Qty 1 × Qty 2 × Rate."
                : "Upload the RKF Working template (.xlsx recommended, .csv accepted). Amount-driven — Qty/Rate columns are ignored. Sub-Type Codes must belong to this project's sanctioned budget."}
            </div>
            <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden"
              onChange={(e) => e.target.files?.[0] && runPreview(e.target.files[0])} data-testid={`v2-${kind}-file-input`} />
            <Button onClick={() => fileRef.current?.click()} data-testid={`v2-${kind}-file-btn`}>
              <FileUp className="w-4 h-4 mr-2" /> Choose XLSX / CSV
            </Button>
          </div>
        )}

        {step === "review" && preview && (
          <div className="flex flex-col gap-4 overflow-hidden">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              <SumChip label="Rows" value={c.total} tone="slate" />
              {isSanc ? (
                <>
                  <SumChip label="OK" value={c.ok || 0} tone="emerald" />
                  <SumChip label="Added in revision" value={c.new || 0} tone="amber" />
                  <SumChip label="Errors" value={c.error || 0} tone="rose" />
                  <SumChip label="Project total" value={rupee(t.project_total)} tone="slate" />
                </>
              ) : (
                <>
                  <SumChip label="Matched" value={c.matched || 0} tone="emerald" />
                  <SumChip label="Errors" value={c.error || 0} tone="rose" />
                  <SumChip label="Unchanged" value={c.unchanged || 0} tone="slate" />
                  <SumChip label="Working total" value={rupee(t.project_total_file)} tone={t.over_sanctioned ? "rose" : "slate"} />
                </>
              )}
            </div>

            {/* Head Review banner — must clear before import can proceed */}
            {isSanc && needsHeadReview && (
              <div className="rounded border border-amber-300 bg-amber-50 p-3 flex items-start gap-3" data-testid="v2-head-review-banner">
                <UserCheck className="w-4 h-4 mt-0.5 text-amber-700" />
                <div className="flex-1 text-xs text-amber-900">
                  <div className="font-semibold mb-0.5">Head Review required</div>
                  <div>
                    {pendingHeads.length} head code{pendingHeads.length === 1 ? "" : "s"} in this file
                    {" "}({pendingHeads.map(h => h.code).join(", ")}){" "}
                    aren't approved for this project yet. Approve their category + name before import.
                  </div>
                </div>
                <Button size="sm" onClick={() => setHeadReviewOpen(true)} data-testid="v2-open-head-review-btn">
                  Review heads
                </Button>
              </div>
            )}


            {/* Sanctioned: ADM & CONT % */}
            {isSanc && t.adm && (
              <div className="flex gap-2 text-xs">
                <div className={`px-2 py-1 rounded border ${t.adm.warning ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-emerald-50 border-emerald-300 text-emerald-700"}`}>
                  ADM {t.adm.percent}% (cap {t.adm.cap}%) · {rupee(t.adm.amount)} of {rupee(t.adm.base)}
                </div>
                <div className={`px-2 py-1 rounded border ${t.cont.warning ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-emerald-50 border-emerald-300 text-emerald-700"}`}>
                  CONT {t.cont.percent}% (cap {t.cont.cap}%)
                </div>
              </div>
            )}
            {/* Working: head variances */}
            {!isSanc && t.head_variances && (
              <div className="flex flex-wrap gap-1.5 text-[11px]">
                {t.head_variances.map(v => (
                  <span key={v.head_code} className={`px-2 py-0.5 rounded border ${v.delta === 0 ? "bg-slate-50 border-slate-200 text-slate-700" : v.delta > 0 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-blue-50 border-blue-300 text-blue-700"}`}>
                    {v.head_code}: {v.delta > 0 ? "+" : ""}{rupee(v.delta)}
                  </span>
                ))}
              </div>
            )}

            {/* Filters */}
            <div className="flex gap-1.5 flex-wrap items-center">
              <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")} data-testid="v2-filter-all">All ({c.total || 0})</Button>
              {isSanc && (c.new || 0) > 0 && <Button size="sm" variant={filter === "new" ? "default" : "outline"} onClick={() => setFilter("new")} data-testid="v2-filter-new">Added in revision ({c.new})</Button>}
              <Button size="sm" variant={filter === "error" ? "default" : "outline"} onClick={() => setFilter("error")} data-testid="v2-filter-error">Errors ({c.error || 0})</Button>
              {!isSanc && <Button size="sm" variant={filter === "unchanged" ? "default" : "outline"} onClick={() => setFilter("unchanged")} data-testid="v2-filter-unchanged">Unchanged ({c.unchanged || 0})</Button>}
              {isSanc && (preview.missing_codes || []).length > 0 && (
                <span className="text-[11px] text-slate-500 ml-2">
                  Not in file: {preview.missing_codes.length} sub-type(s) — will remain unchanged
                </span>
              )}
            </div>

            {/* Table */}
            <div className="overflow-auto border rounded max-h-[52vh]">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="border-b">
                    <th className="text-left px-2 py-1.5 w-10">#</th>
                    <th className="text-left px-2 py-1.5">Period</th>
                    <th className="text-left px-2 py-1.5">Sub-Type Code</th>
                    <th className="text-left px-2 py-1.5">Name</th>
                    <th className="text-right px-2 py-1.5">Amount</th>
                    {isSanc && <th className="text-right px-2 py-1.5">Qty×Rate</th>}
                    <th className="text-left px-2 py-1.5">Status</th>
                    {isSanc && <th className="text-left px-2 py-1.5">Notes</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map(r => (
                    <tr key={r.row_index} className={`border-b ${r.status === "ERROR" ? "bg-rose-50/50" : r.added_in_revision ? "bg-amber-50/40" : ""}`}
                        data-testid={`v2-row-${r.row_index}`}>
                      <td className="px-2 py-1.5 text-slate-500">{r.row_index}</td>
                      <td className="px-2 py-1.5">{r.period_label}</td>
                      <td className="px-2 py-1.5 font-mono">{r.sub_type_code}</td>
                      <td className="px-2 py-1.5">{r.sub_type_name}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{rupee(r.amount)}</td>
                      {isSanc && <td className="px-2 py-1.5 text-right font-mono text-slate-500">{rupee(r.calc_amount)}</td>}
                      <td className="px-2 py-1.5">
                        <StatusChip s={r.status} />
                        {r.added_in_revision && <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] bg-amber-100 text-amber-800 border-amber-300">NEW</span>}
                      </td>
                      {isSanc && (
                        <td className="px-2 py-1.5 text-[11px] text-slate-500">
                          {(r.errors || []).slice(0, 1).join("; ") || (r.overrides || []).slice(0, 1).join("; ") || "—"}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {step === "done" && (
          <ImportSummary result={confirmResult} kind={kind} />
        )}

        <DialogFooter>
          {step === "review" && (
            <Button onClick={doImport} disabled={!canImport || importing} data-testid={`v2-${kind}-import-btn`}>
              {importing ? "Working…" : isSanc ? "Import Sanctioned" : "Create draft for approval"}
            </Button>
          )}
          {step === "done" && <Button onClick={() => onOpenChange(false)} data-testid="v2-close-btn">Close</Button>}
        </DialogFooter>
      </DialogContent>

      {/* Head Review dialog — opened when the file uses codes not yet on the project */}
      {isSanc && (
        <HeadReviewDialog
          projectId={projectId}
          projectCode={projectCode}
          open={headReviewOpen}
          onOpenChange={setHeadReviewOpen}
          pendingHeads={pendingHeads}
          onApproved={async () => {
            // Refresh preview so `needs_head_review` clears + validation reruns
            if (file) await runPreview(file);
          }}
        />
      )}
    </Dialog>
  );
}

function SumChip({ label, value, tone, testid }) {
  const toneCls = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-800",
    amber: "bg-amber-50 border-amber-300 text-amber-800",
    rose: "bg-rose-50 border-rose-300 text-rose-800",
  }[tone] || "bg-slate-50 border-slate-200";
  return (
    <div className={`px-2 py-1 rounded border ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
      <div className="font-mono font-bold text-sm">{value}</div>
    </div>
  );
}

function StatusChip({ s }) {
  const map = {
    OK: ["bg-emerald-100 text-emerald-800 border-emerald-300", <CheckCircle2 className="w-3 h-3" key="i" />],
    MATCHED: ["bg-emerald-100 text-emerald-800 border-emerald-300", <CheckCircle2 className="w-3 h-3" key="i" />],
    AMOUNT_MISMATCH: ["bg-amber-100 text-amber-800 border-amber-300", <AlertTriangle className="w-3 h-3" key="i" />],
    ERROR: ["bg-rose-100 text-rose-800 border-rose-300", <X className="w-3 h-3" key="i" />],
    WARNING: ["bg-amber-100 text-amber-800 border-amber-300", <AlertTriangle className="w-3 h-3" key="i" />],
    UNCHANGED: ["bg-slate-100 text-slate-700 border-slate-300", <Info className="w-3 h-3" key="i" />],
  };
  const [cls, icon] = map[s] || ["bg-slate-100 text-slate-700", null];
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] ${cls}`}>{icon}{s}</span>;
}

function ImportSummary({ result, kind }) {
  if (!result) {
    return (
      <div className="p-8 text-center" data-testid="v2-import-done">
        <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
        <div className="mt-3 font-medium">Import complete</div>
      </div>
    );
  }
  const s = result.summary || {};
  const adm = s.adm || {};
  const cont = s.cont || {};
  const byHead = s.by_head || [];
  const totalHeads = new Set(byHead.map((h) => h.code)).size;
  return (
    <div className="overflow-auto max-h-[60vh] pr-1" data-testid="v2-import-summary">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle2 className="w-6 h-6 text-emerald-500" />
        <div>
          <div className="font-medium">
            Import complete · {s.project_code}
            {s.is_revision ? (
              <span className="ml-2 text-[11px] font-mono px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-300">REVISION</span>
            ) : (
              <span className="ml-2 text-[11px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">INITIAL</span>
            )}
          </div>
          <div className="text-xs text-slate-500">{s.project_name}</div>
        </div>
      </div>

      {/* Row-1 KPIs — heads, sub-types, allocations, project total */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <SumChip label="Heads" value={s.heads_created ?? totalHeads} tone="emerald" testid="sum-heads" />
        <SumChip
          label="Sub-Types"
          value={`${s.sub_types_created ?? 0}${s.sub_types_updated ? ` + ${s.sub_types_updated} upd` : ""}`}
          tone="emerald"
          testid="sum-subtypes"
        />
        <SumChip
          label="Allocations"
          value={`${s.allocations_created ?? 0}${s.allocations_updated ? ` + ${s.allocations_updated} upd` : ""}`}
          tone="emerald"
          testid="sum-allocations"
        />
        <SumChip label="Project Total" value={rupee(s.project_total)} tone="slate" testid="sum-total" />
      </div>

      {/* Row-2 ADM / CONT gauges vs cap */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        <CapGauge label="ADM" data={adm} testid="cap-adm" />
        <CapGauge label="CONT" data={cont} testid="cap-cont" />
      </div>

      {/* Row-3 By-head breakdown */}
      {byHead.length > 0 && (
        <div className="border rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="border-b text-slate-600">
                <th className="text-left px-2 py-1.5 w-20">Head</th>
                <th className="text-left px-2 py-1.5 w-24">Category</th>
                <th className="text-left px-2 py-1.5">Name</th>
                <th className="text-right px-2 py-1.5 w-32">Amount</th>
                <th className="text-right px-2 py-1.5 w-16">% of ₹</th>
              </tr>
            </thead>
            <tbody>
              {byHead.map((h) => {
                const pct = s.project_total ? Math.round((h.amount / s.project_total) * 1000) / 10 : 0;
                return (
                  <tr key={h.code} className="border-b last:border-0" data-testid={`sum-head-${h.code}`}>
                    <td className="px-2 py-1 font-mono">{h.code}</td>
                    <td className="px-2 py-1 text-slate-500 font-mono">{h.category_code}</td>
                    <td className="px-2 py-1">{h.name || "—"}</td>
                    <td className="px-2 py-1 text-right font-mono">{rupee(h.amount)}</td>
                    <td className="px-2 py-1 text-right font-mono text-slate-500">{pct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(s.added_in_revision || []).length > 0 && (
        <div className="mt-3 text-xs bg-indigo-50 border border-indigo-200 rounded p-2" data-testid="sum-added-in-revision">
          <div className="font-medium text-indigo-900">
            {s.added_in_revision.length} sub-type{s.added_in_revision.length === 1 ? "" : "s"} added in this revision
          </div>
          <div className="font-mono text-indigo-700 mt-1 text-[11px]">
            {s.added_in_revision.join(", ")}
          </div>
        </div>
      )}
    </div>
  );
}

function CapGauge({ label, data, testid }) {
  const pct = data?.percent ?? 0;
  const cap = data?.cap ?? 0;
  const warning = data?.warning;
  const tone = warning ? "amber" : "emerald";
  const toneCls = {
    amber: "bg-amber-50 border-amber-300 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-900",
  }[tone];
  const barCls = warning ? "bg-amber-500" : "bg-emerald-500";
  const fill = Math.min(100, cap ? (pct / cap) * 100 : 0);
  return (
    <div className={`px-3 py-2 rounded border ${toneCls}`} data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-wider font-semibold">{label}</div>
        <div className="text-[10px]">
          Cap {cap}% {warning ? "· OVER" : "· OK"}
        </div>
      </div>
      <div className="flex items-baseline gap-2 mt-0.5">
        <div className="font-mono font-bold text-base">{pct}%</div>
        <div className="text-[11px] font-mono">{rupee(data?.amount || 0)}</div>
      </div>
      <div className="h-1.5 bg-white/60 rounded-full mt-1.5 overflow-hidden">
        <div className={`h-full ${barCls} transition-all`} style={{ width: `${fill}%` }} />
      </div>
    </div>
  );
}
